import type { TowerFamilyDefinition } from './types';

/**
 * Each family is one element and one rarity. Rarity is the strength of the trick,
 * not the element: every element shows up in more than one tier.
 */
export const FAMILIES: TowerFamilyDefinition[] = [
  {
    id: 'vanguard',
    name: 'Vanguard',
    faction: 'purple',
    weaponSlot: 'a',
    color: '#9bb2ca',
    accent: '#dce8f0',
    role: 'Frontline',
    rarity: 'common',
    description: 'Reliable bolts. Musters Pikemen who brace in a fight and take less damage while they are hitting.',
    tower: { attack: 'bolt', damage: 7, fireInterval: 0.85, range: 350 },
    spawnInterval: 7.0,
    minion: {
      name: 'Pikeman',
      kind: 'melee',
      hp: 90,
      damage: 13,
      attackInterval: 0.8,
      range: 22,
      speed: 42,
      mass: 1.2,
      bodySprite: 'body',
      scale: 1,
      special: { guard: 0.18 },
      description: 'Braces while fighting. Incoming hits land lighter.',
    },
  },
  {
    id: 'pyre',
    name: 'Pyre',
    faction: 'red',
    weaponSlot: 'b',
    color: '#e95832',
    accent: '#ffac4b',
    role: 'Burst',
    rarity: 'common',
    description: 'A small fireball. Musters Firespitters that cough a short burst of flame.',
    tower: { attack: 'fireball', damage: 7, fireInterval: 1.7, range: 340, splash: 26 },
    spawnInterval: 8.5,
    minion: {
      name: 'Firespitter',
      kind: 'firespitter',
      hp: 72,
      damage: 8,
      attackInterval: 0.9,
      range: 22,
      speed: 40,
      mass: 1,
      bodySprite: 'bodyHalf',
      scale: 1,
      special: { spitRange: 80, spitCooldown: 3.2, spitDamage: 14, spitSplash: 22 },
      description: 'Spits a short puff of fire at the group in front.',
    },
  },
  {
    id: 'frost',
    name: 'Frost',
    faction: 'blue',
    weaponSlot: 'a',
    color: '#47a8d5',
    accent: '#f0fdff',
    role: 'Control',
    rarity: 'common',
    description: 'Bolts that slow whatever they hit. Musters Archers.',
    tower: { attack: 'frost', damage: 5, fireInterval: 0.95, range: 370, slow: 0.45, slowDuration: 1.3 },
    spawnInterval: 7.5,
    minion: {
      name: 'Archer',
      kind: 'ranged',
      hp: 58,
      damage: 14,
      attackInterval: 1.05,
      range: 100,
      speed: 40,
      mass: 0.75,
      bodySprite: 'body',
      scale: 0.95,
      special: {},
      description: 'Shoots from behind the line.',
    },
  },
  {
    id: 'grove',
    name: 'Grove',
    faction: 'green',
    weaponSlot: 'b',
    color: '#92a956',
    accent: '#eff6b0',
    role: 'Sustain',
    rarity: 'epic',
    description: 'A focused beam that melts the closest threat. Musters Healers that mend the push and leave healing pools.',
    tower: { attack: 'laser', damage: 1.8, fireInterval: 0.22, range: 330 },
    spawnInterval: 9.0,
    minion: {
      name: 'Healer',
      kind: 'healer',
      hp: 82,
      damage: 6,
      attackInterval: 1.0,
      range: 22,
      speed: 38,
      mass: 0.9,
      bodySprite: 'body',
      scale: 0.95,
      special: { healAmount: 13, healCooldown: 1.3, healRange: 74, poolHeal: 7, poolDuration: 5, poolRadius: 58 },
      description: 'Mends the most wounded ally nearby. Leaves a healing pool when destroyed.',
    },
  },
  {
    id: 'bastion',
    name: 'Bastion',
    faction: 'sand',
    weaponSlot: 'a',
    color: '#a39764',
    accent: '#d7cb91',
    role: 'Support',
    rarity: 'rare',
    description: 'A modest aura for the towers beside it. Musters Tanks that shove the enemy line once they are wounded.',
    tower: { attack: 'support', damage: 0, fireInterval: 1, range: 0, support: 0.08 },
    spawnInterval: 10.0,
    minion: {
      name: 'Tank',
      kind: 'tank',
      hp: 230,
      damage: 14,
      attackInterval: 1.2,
      range: 24,
      speed: 30,
      mass: 2.6,
      bodySprite: 'bodyHalf',
      scale: 1.15,
      special: { ignoreChance: 0.12, shoveDistance: 42, shoveThreshold: 0.5 },
      description: 'Shrugs off some hits. The first time it is badly hurt, it shoves the enemies in front.',
    },
  },
  {
    id: 'bombard',
    name: 'Bombard',
    faction: 'gold',
    weaponSlot: 'b',
    color: '#d9442f',
    accent: '#fff1a0',
    role: 'Tempo',
    rarity: 'legendary',
    description: 'Long-range mortar that lands on a random enemy. Musters fast Sappers that detonate in a wide blast.',
    tower: { attack: 'mortar', damage: 16, fireInterval: 2.3, range: 600, splash: 48 },
    spawnInterval: 8.0,
    minion: {
      name: 'Sapper',
      kind: 'sapper',
      hp: 46,
      damage: 0,
      attackInterval: 1,
      range: 20,
      speed: 64,
      mass: 0.65,
      bodySprite: 'body',
      scale: 0.85,
      special: { blastDamage: 68, blastRadius: 50 },
      description: 'Fast. Detonates on the first enemy it touches, in a wide blast.',
    },
  },
  {
    id: 'skirmisher',
    name: 'Skirmish',
    faction: 'skirmish',
    weaponSlot: 'b',
    color: '#8fa9c5',
    accent: '#eef5fb',
    role: 'Flanker',
    rarity: 'rare',
    sigil: '»',
    description: 'A light cannon. Musters Runners that reach the fight first and hit hardest on the opening blow.',
    tower: { attack: 'bolt', damage: 5, fireInterval: 0.7, range: 320 },
    spawnInterval: 6.4,
    minion: {
      name: 'Runner',
      kind: 'melee',
      hp: 48,
      damage: 11,
      attackInterval: 0.65,
      range: 20,
      speed: 72,
      mass: 0.5,
      bodySprite: 'body',
      scale: 0.78,
      special: { ambush: 1.7 },
      description: 'Fast and fragile. The first blow is the heavy one.',
    },
  },
  {
    id: 'hexer',
    name: 'Hex',
    faction: 'hex',
    weaponSlot: 'a',
    color: '#285a8c',
    accent: '#a5e7f5',
    role: 'Curse',
    rarity: 'rare',
    sigil: '✧',
    description: 'A soft bolt. Musters Cursers whose arrows make a target hit softer and drag its steps.',
    tower: { attack: 'frost', damage: 4, fireInterval: 1.05, range: 360, slow: 0.3, slowDuration: 1.2 },
    spawnInterval: 8.2,
    minion: {
      name: 'Curser',
      kind: 'ranged',
      hp: 54,
      damage: 9,
      attackInterval: 1.15,
      range: 110,
      speed: 36,
      mass: 0.8,
      bodySprite: 'body',
      scale: 0.9,
      special: { curseSlow: 0.35, curseDuration: 2, curseDamage: 0.25 },
      description: 'Each hit slows the target and makes its own attacks land softer.',
    },
  },
  {
    id: 'banner',
    name: 'Banner',
    faction: 'gold',
    weaponSlot: 'a',
    color: '#c6d6e6',
    accent: '#ffffff',
    role: 'Standard',
    rarity: 'legendary',
    sigil: '▲',
    description: 'A strong aura for the towers beside it. Musters Standards that make nearby allies hit harder.',
    tower: { attack: 'support', damage: 0, fireInterval: 1, range: 0, support: 0.2 },
    spawnInterval: 8.4,
    minion: {
      name: 'Standard',
      kind: 'melee',
      hp: 78,
      damage: 11,
      attackInterval: 0.9,
      range: 22,
      speed: 44,
      mass: 1.05,
      bodySprite: 'body',
      scale: 1,
      special: { inspire: 0.18 },
      description: 'Allies beside it hit harder. The banner is the gift.',
    },
  },
  {
    id: 'outrider',
    name: 'Outrider',
    faction: 'skirmish',
    weaponSlot: 'a',
    color: '#b6cbdf',
    accent: '#ffffff',
    role: 'Scout',
    rarity: 'epic',
    sigil: '↗',
    description: 'A quick bolt. Musters Outriders that enter far up the lane and strike hard on the first blow.',
    tower: { attack: 'bolt', damage: 6, fireInterval: 0.75, range: 330 },
    spawnInterval: 6.2,
    minion: {
      name: 'Outrider',
      kind: 'melee',
      hp: 42,
      damage: 12,
      attackInterval: 0.62,
      range: 20,
      speed: 80,
      mass: 0.55,
      bodySprite: 'body',
      scale: 0.8,
      special: { deploy: 170, ambush: 1.35 },
      description: 'Spawns far up the lane. The first blow is heavy, then it is fragile.',
    },
  },
  {
    id: 'bulwark',
    name: 'Bulwark',
    faction: 'sand',
    weaponSlot: 'b',
    color: '#7c8750',
    accent: '#e4dab0',
    role: 'Anchor',
    rarity: 'legendary',
    sigil: '◆',
    description: 'A heavy bolt. Musters Bulwarks, the densest body in the file. They do not give ground.',
    tower: { attack: 'bolt', damage: 8, fireInterval: 1.05, range: 300 },
    spawnInterval: 11,
    minion: {
      name: 'Bulwark',
      kind: 'melee',
      hp: 280,
      damage: 16,
      attackInterval: 1.15,
      range: 24,
      speed: 26,
      mass: 3.1,
      bodySprite: 'bodyHalf',
      scale: 1.12,
      special: { anchor: 0.35 },
      description: 'Slow and enormous. Enemies stuck to it swing slower.',
    },
  },
  {
    id: 'siege',
    name: 'Siege',
    faction: 'red',
    weaponSlot: 'a',
    color: '#c95232',
    accent: '#ffc35b',
    role: 'Ram',
    rarity: 'epic',
    sigil: '✸',
    description: 'A mortar. Musters Rams. Each one that reaches a fortress takes two hearts.',
    tower: { attack: 'mortar', damage: 15, fireInterval: 2.6, range: 560, splash: 36 },
    spawnInterval: 12,
    minion: {
      name: 'Ram',
      kind: 'melee',
      hp: 170,
      damage: 18,
      attackInterval: 1.05,
      range: 22,
      speed: 26,
      mass: 2.4,
      bodySprite: 'bodyHalf',
      scale: 1.08,
      special: { leak: 2 },
      description: 'Slow. Reaching the fortress costs them two hearts, not one.',
    },
  },
  {
    id: 'pebble', name: 'Pebble', faction: 'green', weaponSlot: 'a',
    color: '#92a956', accent: '#d7cb91', role: 'Volley', rarity: 'common',
    description: 'A stone launcher. Musters Slingers that keep steady pressure from behind the front line.',
    tower: { attack: 'bolt', damage: 6, fireInterval: 0.8, range: 320 }, spawnInterval: 7.8,
    minion: { name: 'Slinger', kind: 'ranged', hp: 65, damage: 11, attackInterval: 0.85,
      range: 85, speed: 39, mass: 0.85, bodySprite: 'body', scale: 0.95, special: {},
      description: 'A sturdy ranged fighter with a short, steady volley.' },
  },
  {
    id: 'brook', name: 'Brook', faction: 'blue', weaponSlot: 'b',
    color: '#47a8d5', accent: '#a5e7f5', role: 'Guard', rarity: 'common',
    description: 'A quick water bolt. Musters Brookguards that brace while fighting.',
    tower: { attack: 'bolt', damage: 5, fireInterval: 0.75, range: 320 }, spawnInterval: 8.2,
    minion: { name: 'Brookguard', kind: 'melee', hp: 112, damage: 10, attackInterval: 0.95,
      range: 22, speed: 36, mass: 1.45, bodySprite: 'body', scale: 1, special: { guard: 0.12 },
      description: 'A water-armored guard. Takes 12% less damage while engaged.' },
  },
  {
    id: 'cinder', name: 'Cinder', faction: 'red', weaponSlot: 'b',
    color: '#e95832', accent: '#ffac4b', role: 'Demolition', rarity: 'rare',
    description: 'A close-range fire mortar. Musters Coalings that explode on contact.',
    tower: { attack: 'fireball', damage: 10, fireInterval: 1.9, range: 310, splash: 30 }, spawnInterval: 8.8,
    minion: { name: 'Coaling', kind: 'sapper', hp: 65, damage: 0, attackInterval: 1,
      range: 20, speed: 48, mass: 0.9, bodySprite: 'body', scale: 0.95,
      special: { blastDamage: 48, blastRadius: 34 },
      description: 'A tough little coal charge. Explodes on the first enemy it reaches.' },
  },
  {
    id: 'tempest', name: 'Tempest', faction: 'skirmish', weaponSlot: 'a',
    color: '#9bb2ca', accent: '#ffffff', role: 'Ambush', rarity: 'rare',
    description: 'A long-reaching bolt. Musters Gale Archers with a powerful opening shot.',
    tower: { attack: 'bolt', damage: 9, fireInterval: 1.25, range: 395 }, spawnInterval: 8.4,
    minion: { name: 'Gale Archer', kind: 'ranged', hp: 46, damage: 12, attackInterval: 1.15,
      range: 112, speed: 48, mass: 0.65, bodySprite: 'body', scale: 0.9,
      special: { ambush: 1.6 }, description: 'A swift, fragile archer. Its opening shot deals 60% extra damage.' },
  },

];

export const FAMILY_BY_ID: Record<string, TowerFamilyDefinition> = Object.fromEntries(
  FAMILIES.map((f) => [f.id, f]),
);

export const RARITY_LABEL = {
  common: 'Common',
  rare: 'Rare',
  epic: 'Epic',
  legendary: 'Legendary',
} as const;
